import React, { useState, useEffect, useContext } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Title,
  Legend,
  Tooltip,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { DataContext } from "../../../context/DataContext";
import { getToken } from "../../../api/Account";
import { getColocacionSemanalAsync } from "../../../api/Dashboard";
import { simpleMessage } from "../../../helpers/Helpers";
import { chartTextColor, chartGridColor } from "../../../theme";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Title,
  Legend,
  Tooltip
);

const money = (value) =>
  new Intl.NumberFormat("es-NI", {
    style: "currency",
    currency: "NIO",
  }).format(value);

const moneyShort = (value) =>
  new Intl.NumberFormat("es-NI", {
    style: "currency",
    currency: "NIO",
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value);

const ColocacionSemanal = ({ selectedSucursal }) => {
  const [labels, setLabels] = useState([]);
  const [colocacion, setColocacion] = useState([]);

  const { setIsLoading, setIsUnautorized, isDarkMode } = useContext(DataContext);
  const dayName = (fecha) =>
    ["domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado"][
      new Date(fecha).getDay()
    ];

  const tick = chartTextColor(isDarkMode);
  const grid = chartGridColor(isDarkMode);

  // Linea con degradado verde, como en el mockup del Dashboard.
  const graphicData = {
    labels: labels,
    datasets: [
      {
        label: "Colocación",
        data: colocacion,
        borderColor: "#12a35a",
        backgroundColor: (context) => {
          const { ctx, chartArea } = context.chart;
          if (!chartArea) {
            return "rgba(18,163,90,.18)";
          }
          const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
          gradient.addColorStop(0, "rgba(18,163,90,.35)");
          gradient.addColorStop(1, "rgba(18,163,90,0)");
          return gradient;
        },
        fill: true,
        pointBackgroundColor: "#12a35a",
        pointBorderColor: "#fff",
        pointBorderWidth: 2,
        pointStyle: "circle",
        pointRadius: 5,
        pointHoverRadius: 7,
        tension: 0.35,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      tooltip: {
        enabled: true,
        callbacks: {
          label: function (context) {
            let label = context.dataset.label || "";

            if (label) {
              label += ": ";
            }
            if (context.parsed.y !== null) {
              label += money(context.parsed.y);
            }
            return label;
          },
        },
      },

      legend: { display: false },
      title: { display: false },
    },

    elements: {
      line: {
        borderJoinStyle: "round",
      },
    },

    scales: {
      x: {
        ticks: { color: tick },
        grid: { display: false },
      },
      y: {
        ticks: {
          color: tick,
          callback: (value) => moneyShort(value),
        },
        grid: { color: grid },
        beginAtZero: true,
      },
    },
  };

  const token = getToken();

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const result = await getColocacionSemanalAsync(token, selectedSucursal);
      if (!result.statusResponse) {
        setIsLoading(false);
        if (result.error === "Unauthorized") {
          setIsUnautorized(true);
          return;
        }
        simpleMessage(result.error, "error");
        return;
      }

      let dias = [];
      let coloc = [];

      result.data.result.forEach((item) => {
        dias.push(dayName(item.fecha));
        coloc.push(item.colocacion);
      });

      setLabels(dias);
      setColocacion(coloc);

      setIsLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSucursal]);

  return (
    <div style={{ height: "100%" }}>
      <Line options={options} data={graphicData} />
    </div>
  );
};

export default ColocacionSemanal;
